import { useEffect, useState, type ClipboardEvent, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';

import type {
  AdminQuestionnaireTemplateViewModel,
  UpdateAdminQuestionnaireTemplateInput,
} from '../api';
import {
  applyJsonToTemplateValues,
  isRecord,
  templateValuesToJson,
  type TemplateClipboardValues,
} from '../clipboard';
import { QuestionnaireEmailFields } from './QuestionnaireEmailFields';

interface FieldErrors {
  label?: string;
  templateKey?: string;
}

type ClipboardNote = { readonly kind: 'ok' | 'error'; readonly text: string } | null;

export function EditQuestionnaireTemplateDialog({
  row,
  onCancel,
  onSubmit,
  pending,
  submitError,
}: {
  readonly row: AdminQuestionnaireTemplateViewModel | null;
  readonly onCancel: () => void;
  readonly onSubmit: (input: UpdateAdminQuestionnaireTemplateInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [values, setValues] = useState<TemplateClipboardValues>({
    label: '',
    templateKey: '',
    active: true,
    emailSubject: '',
    emailBodyHtml: '',
    emailBodyText: '',
    emailServerId: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [copied, setCopied] = useState(false);
  const [clipboardNote, setClipboardNote] = useState<ClipboardNote>(null);

  useEffect(() => {
    if (row !== null) {
      setValues({
        label: row.label,
        templateKey: row.templateKey,
        active: row.active,
        emailSubject: row.emailSubject,
        emailBodyHtml: row.emailBodyHtml,
        emailBodyText: row.emailBodyText,
        emailServerId: row.emailServerId,
      });
      setErrors({});
      setCopied(false);
      setClipboardNote(null);
    }
  }, [row]);

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(templateValuesToJson(values), null, 2));
      setCopied(true);
      setClipboardNote(null);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      setClipboardNote({ kind: 'error', text: t('routes.admin.questionnaires.templates.form.clipboard.copyError') });
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLFormElement>): void {
    const text = event.clipboardData.getData('text');
    if (text.trim().length === 0) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return;
    }
    if (!isRecord(parsed)) return;
    const { values: nextValues, applied } = applyJsonToTemplateValues(values, parsed);
    if (applied === 0) return;
    event.preventDefault();
    setValues(nextValues);
    setErrors({});
    setClipboardNote({
      kind: 'ok',
      text: t('routes.admin.questionnaires.templates.form.clipboard.imported', { count: applied }),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (row === null) return;
    const labelTrimmed = values.label.trim();
    const keyTrimmed = values.templateKey.trim();
    const next: FieldErrors = {};
    if (labelTrimmed.length === 0) next.label = t('routes.admin.questionnaires.templates.form.errors.labelRequired');
    else if (labelTrimmed.length > 255) next.label = t('routes.admin.questionnaires.templates.form.errors.labelTooLong');
    if (keyTrimmed.length === 0)
      next.templateKey = t('routes.admin.questionnaires.templates.form.errors.templateKeyRequired');
    else if (keyTrimmed.length > 255)
      next.templateKey = t('routes.admin.questionnaires.templates.form.errors.templateKeyTooLong');
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      id: row.id,
      label: labelTrimmed,
      templateKey: keyTrimmed,
      active: values.active,
      emailSubject: values.emailSubject,
      emailBodyHtml: values.emailBodyHtml,
      emailBodyText: values.emailBodyText,
      emailServerId: values.emailServerId,
    });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-[54rem]" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.admin.questionnaires.templates.edit.title')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          onPaste={handlePaste}
          noValidate
          className="space-y-4 max-h-[75vh] overflow-y-auto pr-1"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField
              id="questionnaire-template-edit-label"
              label={t('routes.admin.questionnaires.templates.form.label')}
              required
              error={errors.label}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={values.label}
                  onChange={(e) => {
                    setValues((p) => ({ ...p, label: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
            </FormField>
            <FormField
              id="questionnaire-template-edit-key"
              label={t('routes.admin.questionnaires.templates.form.templateKey')}
              required
              error={errors.templateKey}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={values.templateKey}
                  onChange={(e) => {
                    setValues((p) => ({ ...p, templateKey: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <label className="inline-flex items-center gap-2 text-sm sm:pt-7">
              <input
                type="checkbox"
                checked={values.active}
                onChange={(e) => {
                  setValues((p) => ({ ...p, active: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.questionnaires.templates.form.active')}
            </label>
          </div>

          <QuestionnaireEmailFields
            idPrefix="qt-edit"
            values={{
              emailSubject: values.emailSubject,
              emailBodyHtml: values.emailBodyHtml,
              emailBodyText: values.emailBodyText,
              emailServerId: values.emailServerId,
            }}
            onChange={(field, value) => {
              setValues((p) => ({ ...p, [field]: value }));
            }}
          />
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.questionnaires.templates.form.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter className="sm:items-center sm:justify-between sm:space-x-0">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied
                  ? t('routes.admin.questionnaires.templates.form.clipboard.copied')
                  : t('routes.admin.questionnaires.templates.form.clipboard.copy')}
              </Button>
              {clipboardNote !== null ? (
                <span
                  role={clipboardNote.kind === 'error' ? 'alert' : undefined}
                  className={
                    clipboardNote.kind === 'error' ? 'text-xs text-red-600' : 'text-xs text-muted-foreground'
                  }
                >
                  {clipboardNote.text}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t('routes.admin.questionnaires.templates.form.clipboard.pasteHint')}
                </span>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-2">
              <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
                {t('routes.admin.questionnaires.templates.form.cancel')}
              </Button>
              <Button type="submit" loading={pending}>
                {t('routes.admin.questionnaires.templates.edit.submit')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
